import { useMemo, type ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { AlertCircle, Clock3, UserCheck, UserRoundX } from 'lucide-react'

import { dashboardApi } from '@/services/dashboard'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { cn, formatDate } from '@/lib/utils'
import type { DashboardUpcomingWorkdayScheduleItem, JobRealtimeOnlineWorkerItem, WorkerDayAvailabilityItem, WorkerDayLeaveItem } from '@/types'

const SCHEDULING_TABS = [
  { value: '/job/realtime', label: 'Real Time' },
  { value: '/jobs/scheduling', label: 'Schedule' },
  { value: '/timeline', label: 'Timeline' },
  { value: '/workdays', label: 'Work Days' },
] as const

export default function RealtimeOperationsPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)

  const activeSchedulingTab = useMemo(() => {
    const pathname = location.pathname
    if (pathname.startsWith('/timeline')) return '/timeline'
    if (pathname.startsWith('/workdays')) return '/workdays'
    if (pathname.startsWith('/jobs/scheduling') || pathname.startsWith('/job/scheduling') || pathname.startsWith('/job/schedule-workers')) {
      return '/jobs/scheduling'
    }
    return '/job/realtime'
  }, [location.pathname])

  const handleSchedulingTabNavigate = (nextPath: string) => {
    if (!nextPath || nextPath === activeSchedulingTab) {
      return
    }

    navigate(withServiceInstanceId(nextPath, serviceInstanceId))
  }

  const { data, isLoading, isError } = useQuery({
    queryKey: ['job-realtime-overview'],
    queryFn: dashboardApi.getRealtimeOverview,
  })

  const summary = data?.summary
  const availabilityLookup = buildWorkerAvailabilityLookup(data?.worker_day_availability || [])
  const leaveLookup = buildWorkerLeaveLookup(data?.worker_day_leaves || [])
  const workerRows = buildWorkerRows(data?.live_workday_entries || [], availabilityLookup, leaveLookup)
  const mapCenter = resolveOnlineWorkersMapCenter(data?.online_workers || [])
  const mapBounds = buildOnlineWorkersMapBounds(data?.online_workers || [], mapCenter.lat, mapCenter.lng)
  const mapEmbedUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${mapBounds}&layer=mapnik`
  const alertCount = workerRows.reduce(
    (count, row) => count + row.items.filter((item) => item.isOutsideAvailability).length,
    0,
  )

  if (isLoading) {
    return (
      <div className="space-y-4 p-4 sm:p-6">
        <Skeleton className="h-8 w-56" />
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton key={index} className="h-24 w-full rounded-xl" />
          ))}
        </div>
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="space-y-4 p-4 sm:p-6">
        <h1 className="text-2xl font-semibold">Real-Time</h1>
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            Unable to load realtime operations right now.
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-4 bg-white p-4 sm:p-6">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#dfeae3] bg-[#f7fbf8] p-2">
        {SCHEDULING_TABS.map((tab) => {
          const isActive = activeSchedulingTab === tab.value
          return (
            <Button
              key={tab.value}
              type="button"
              variant="ghost"
              onClick={() => handleSchedulingTabNavigate(tab.value)}
              className={cn(
                'h-8 rounded-md px-3 text-xs font-semibold',
                isActive
                  ? 'bg-[#169b62] text-white hover:bg-[#118753] hover:text-white'
                  : 'text-[#355445] hover:bg-[#e8f4ee]'
              )}
            >
              {tab.label}
            </Button>
          )
        })}
      </div>

      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-[34px] font-semibold tracking-tight text-[#20352b]">Real-Time Worker Scheduling</h1>
          <p className="text-[12px] text-[#718078]">
            Live operations snapshot for today.
          </p>
        </div>
        <Badge className="rounded-full bg-[#ddf6e7] px-4 py-2 text-[11px] font-semibold text-[#087a49] hover:bg-[#ddf6e7]">
          Live Updates • {formatDate(data.generated_at, { hour: '2-digit', minute: '2-digit' })}
        </Badge>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryTile label="Workers On Job" value={summary?.in_progress_jobs_count ?? 0} icon={<UserCheck className="h-4 w-4" />} tone="green" />
        <SummaryTile label="Available Now" value={summary?.online_workers_count ?? 0} icon={<UserRoundX className="h-4 w-4" />} tone="blue" />
        <SummaryTile label="Jobs Unassigned" value={summary?.unassigned_assignments_count ?? 0} icon={<Clock3 className="h-4 w-4" />} tone="amber" />
        <SummaryTile label="Running Late" value={summary?.late_work_day_entries_count ?? 0} icon={<AlertCircle className="h-4 w-4" />} tone="rose" />
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card className="border-[#dfeae3] shadow-none">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-[#20352b]">Unassigned Jobs ({data.unassigned_assignments.length})</CardTitle>
            <Link to="/assignments" className="text-xs font-semibold text-[#169b62]">View all</Link>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.unassigned_assignments.length === 0 ? (
              <p className="text-sm text-muted-foreground">No unassigned assignments.</p>
            ) : (
              data.unassigned_assignments.slice(0, 4).map((item) => (
                <div key={item.id} className="rounded-lg border border-[#dfeae3] p-3">
                  <p className="text-[11px] font-semibold text-[#de3f47]">{item.role_label || 'Unassigned role'}</p>
                  <p className="mt-1 text-sm font-medium text-[#20352b]">{item.job_title || 'Untitled job'}</p>
                  <p className="mt-1 text-xs text-[#718078]">
                    {item.scheduled_start ? formatDate(item.scheduled_start, { hour: '2-digit', minute: '2-digit' }) : 'Today'}
                  </p>
                  <div className="mt-3">
                    <Button asChild size="sm" className="h-7 rounded-md bg-[#169b62] px-3 text-[11px] hover:bg-[#118753]">
                      <Link to="/assignments">Assign</Link>
                    </Button>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card className="border-[#dfeae3] shadow-none">
          <CardContent className="p-0">
            <div className="relative h-[265px] overflow-hidden rounded-[8px] border border-[#dfeae3] bg-[#f2f8f3]">
              <iframe
                title="Realtime worker map"
                src={mapEmbedUrl}
                className="absolute inset-0 h-full w-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div className="absolute bottom-3 right-3 rounded-md bg-white/90 px-2 py-1 text-[10px] text-[#4e6157] shadow-sm">
                {data.online_workers.length} online workers
              </div>
              <a
                href={`https://www.openstreetmap.org/?mlat=${mapCenter.lat}&mlon=${mapCenter.lng}#map=13/${mapCenter.lat}/${mapCenter.lng}`}
                target="_blank"
                rel="noreferrer"
                className="absolute left-3 top-3 rounded-md bg-white/90 px-2 py-1 text-[10px] font-medium text-[#1f5f8f] shadow-sm hover:bg-white"
              >
                Open map
              </a>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-[#dfeae3] shadow-none">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div>
            <CardTitle className="text-base text-[#20352b]">Live Schedule - Today</CardTitle>
            <CardDescription className="text-xs text-[#718078]">{formatDate(new Date())}</CardDescription>
          </div>
          <Badge variant="secondary" className="bg-[#ddf6e7] text-[#087a49]">Today</Badge>
        </CardHeader>
        <CardContent className="space-y-2">
          {alertCount > 0 ? (
            <div className="rounded-md border border-[#f4c8cc] bg-[#fde9eb] px-3 py-2 text-xs text-[#a63c46]">
              <p className="font-semibold">Alert: {alertCount} allocation{alertCount > 1 ? 's are' : ' is'} outside worker availability.</p>
            </div>
          ) : null}
          {workerRows.map((row) => (
            <div key={row.worker} className="grid grid-cols-12 gap-2 rounded-lg p-1">
              <div className="col-span-12 sm:col-span-3 lg:col-span-2">
                <div className="rounded-md p-2">
                  {row.workerId ? (
                    <Link to={`/workers/${row.workerId}`} className="text-sm font-semibold text-[#20352b] underline-offset-2 hover:underline">
                      {row.worker}
                    </Link>
                  ) : (
                    <p className="text-sm font-semibold text-[#20352b]">{row.worker}</p>
                  )}
                  <p className="text-[11px] text-[#718078]">{row.primaryRole}</p>
                </div>
              </div>
              {row.items.slice(0, 3).map((entry) => (
                <div key={entry.id} className={`col-span-6 sm:col-span-3 lg:col-span-3 rounded-md px-2 py-2 text-white ${entry.isOutsideAvailability ? 'bg-[#cb3d4d]' : 'bg-[#2f8fdd]'}`}>
                  <p className="text-[10px] font-medium">{formatEntryTimeRange(entry)}</p>
                  {entry.job_id ? (
                    <Link to={`/jobs/${entry.job_id}`} className="mt-1 line-clamp-2 text-[11px] underline-offset-2 hover:underline">
                      {entry.job_title || 'Scheduled work'}
                    </Link>
                  ) : (
                    <p className="mt-1 line-clamp-2 text-[11px]">{entry.job_title || 'Scheduled work'}</p>
                  )}
                  <p className="mt-1 text-[9px] text-white/90">Avail: {entry.availabilityText}</p>
                  {entry.isOutsideAvailability ? (
                    <p className="mt-1 text-[9px] font-semibold text-white">{entry.isOnLeave ? 'ALERT: Worker on leave' : 'ALERT: Outside availability'}</p>
                  ) : null}
                </div>
              ))}
              <div className="col-span-6 sm:col-span-3 lg:col-span-4 rounded-md bg-[#ddf6e7] px-2 py-2 text-[#087a49]">
                <p className="text-[10px] font-semibold">Available</p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  )
}

function SummaryTile({
  label,
  value,
  icon,
  tone,
}: {
  label: string
  value: number
  icon: ReactNode
  tone: 'green' | 'blue' | 'amber' | 'rose'
}) {
  const tones = {
    green: 'bg-[#ddf6e7] text-[#087a49]',
    blue: 'bg-[#e7f3fd] text-[#2567a8]',
    amber: 'bg-[#fff2de] text-[#b1741b]',
    rose: 'bg-[#fde9eb] text-[#bb4e58]',
  }

  return (
    <Card className="border-[#dfeae3] shadow-none">
      <CardContent className="flex items-center gap-3 p-3">
        <div className={`flex h-8 w-8 items-center justify-center rounded-md ${tones[tone]}`}>
          {icon}
        </div>
        <div>
          <p className="text-[26px] leading-none text-[#20352b]">{value}</p>
          <p className="mt-1 text-[11px] text-[#718078]">{label}</p>
        </div>
      </CardContent>
    </Card>
  )
}

function buildWorkerRows(
  entries: DashboardUpcomingWorkdayScheduleItem[],
  availabilityLookup: Map<string, string>,
  leaveLookup: Set<string>,
) {
  const grouped = new Map<string, DashboardUpcomingWorkdayScheduleItem[]>()
  entries.forEach((entry) => {
    const key = entry.worker_name || 'Unassigned'
    const list = grouped.get(key) ?? []
    list.push(entry)
    grouped.set(key, list)
  })

  const rows = Array.from(grouped.entries()).map(([worker, items]) => {
    const nextItems = items.map((item) => {
      const workDate = resolveEntryDay(item)
      const workerId = item.worker_id ?? null
      const availabilityText = resolveWorkerDayAvailability(availabilityLookup, workerId, workDate)
      const isOnLeave = resolveWorkerOnLeave(leaveLookup, workerId, workDate)
      const isOutsideAvailability = isOnLeave || isScheduleOutsideAvailabilityWindow(formatEntryTimeRange(item), availabilityText)

      return {
        ...item,
        availabilityText: isOnLeave ? 'On Leave' : availabilityText,
        isOnLeave,
        isOutsideAvailability,
      }
    })

    return {
      worker,
      workerId: items.find((item) => item.worker_id != null)?.worker_id ?? null,
      items: nextItems,
      primaryRole: 'Field Worker',
    }
  })

  if (rows.length === 0) {
    return [
      {
        worker: 'No active workers',
        workerId: null,
        primaryRole: 'Live feed',
        items: [],
      },
    ]
  }

  return rows.slice(0, 6)
}

function buildWorkerAvailabilityLookup(items: WorkerDayAvailabilityItem[]) {
  const lookup = new Map<string, string>()

  items.forEach((item) => {
    const workerId = item.worker_id == null ? '' : String(item.worker_id).trim()
    const workDate = String(item.work_date || '').trim()
    if (!workerId || !workDate) {
      return
    }

    const key = `${workerId}|${workDate}`
    if (item.is_available === false) {
      lookup.set(key, '--:--')
      return
    }

    const from = String(item.from_time || '').trim()
    const to = String(item.to_time || '').trim()
    if (!from || !to) {
      if (!lookup.has(key)) {
        lookup.set(key, '--:--')
      }
      return
    }

    const nextValue = `${formatHourMinute(from)}-${formatHourMinute(to)}`
    const existing = lookup.get(key)
    if (!existing || existing === 'Available') {
      lookup.set(key, nextValue)
      return
    }
    if (existing !== 'Unavailable') {
      lookup.set(key, `${existing}, ${nextValue}`)
    }
  })

  return lookup
}

function buildWorkerLeaveLookup(items: WorkerDayLeaveItem[]) {
  const lookup = new Set<string>()

  items.forEach((item) => {
    const workerId = item.worker_id == null ? '' : String(item.worker_id).trim()
    const workDate = String(item.work_date || '').trim()
    if (!workerId || !workDate || item.is_on_leave !== true) {
      return
    }

    lookup.add(`${workerId}|${workDate}`)
  })

  return lookup
}

function resolveWorkerDayAvailability(lookup: Map<string, string>, workerId: number | string | null, day: string) {
  if (workerId == null || !day) {
    return '--:--'
  }
  const key = `${String(workerId).trim()}|${day}`
  return lookup.get(key) || '--:--'
}

function resolveWorkerOnLeave(lookup: Set<string>, workerId: number | string | null, day: string) {
  if (workerId == null || !day) {
    return false
  }
  const key = `${String(workerId).trim()}|${day}`
  return lookup.has(key)
}

function resolveEntryDay(entry: DashboardUpcomingWorkdayScheduleItem) {
  const explicitDay = String(entry.work_date || '').trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(explicitDay)) {
    return explicitDay
  }

  const plannedStart = String(entry.planned_start || '').trim()
  if (!plannedStart) {
    return ''
  }

  if (/^\d{4}-\d{2}-\d{2}/.test(plannedStart)) {
    return plannedStart.slice(0, 10)
  }

  const parsed = new Date(plannedStart)
  if (Number.isNaN(parsed.getTime())) {
    return ''
  }
  return parsed.toISOString().slice(0, 10)
}

function formatHourMinute(value: string) {
  const raw = String(value || '').trim()
  const match = raw.match(/^(\d{1,2}):(\d{2})/)
  if (!match) {
    return raw
  }
  return `${match[1].padStart(2, '0')}:${match[2]}`
}

function isScheduleOutsideAvailabilityWindow(scheduleTime: string, availabilityText: string) {
  const scheduleRange = parseHourMinuteRange(scheduleTime)
  if (!scheduleRange) {
    return false
  }

  const availabilityRaw = String(availabilityText || '').trim()
  if (!availabilityRaw || availabilityRaw === '--:--') {
    return false
  }

  if (availabilityRaw.toLowerCase() === 'unavailable') {
    return true
  }

  const availabilityRanges = availabilityRaw
    .split(',')
    .map((chunk) => parseHourMinuteRange(chunk.trim()))
    .filter((range): range is [number, number] => range != null)

  if (availabilityRanges.length === 0) {
    return false
  }

  return !availabilityRanges.some(([availableStart, availableEnd]) => {
    const [scheduledStart, scheduledEnd] = scheduleRange
    return scheduledStart >= availableStart && scheduledEnd <= availableEnd
  })
}

function parseHourMinuteRange(value: string): [number, number] | null {
  const raw = String(value || '').trim()
  const [startRaw, endRaw] = raw.split('-').map((part) => part.trim())
  if (!startRaw || !endRaw) {
    return null
  }

  const start = parseHourMinuteToMinutes(startRaw)
  const end = parseHourMinuteToMinutes(endRaw)
  if (start == null || end == null || end <= start) {
    return null
  }

  return [start, end]
}

function parseHourMinuteToMinutes(value: string): number | null {
  const match = String(value || '').trim().match(/^(\d{1,2}):(\d{2})$/)
  if (!match) {
    return null
  }

  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) {
    return null
  }
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null
  }

  return hours * 60 + minutes
}

function resolveOnlineWorkersMapCenter(workers: JobRealtimeOnlineWorkerItem[]) {
  const coordinates = workers
    .map((worker) => ({
      lat: toMapCoordinate(worker.latitude),
      lng: toMapCoordinate(worker.longitude),
    }))
    .filter((entry): entry is { lat: number; lng: number } => entry.lat != null && entry.lng != null)

  if (coordinates.length > 0) {
    const centerLat = coordinates.reduce((sum, entry) => sum + entry.lat, 0) / coordinates.length
    const centerLng = coordinates.reduce((sum, entry) => sum + entry.lng, 0) / coordinates.length
    return { lat: centerLat, lng: centerLng }
  }

  // Fallback center when online workers do not include coordinates.
  return { lat: 20.5937, lng: 78.9629 }
}

function toMapCoordinate(value: unknown) {
  if (value == null || value === '') {
    return null
  }
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) {
    return null
  }
  return parsed
}

function buildOnlineWorkersMapBounds(workers: JobRealtimeOnlineWorkerItem[], fallbackLat: number, fallbackLng: number) {
  const coordinates = workers
    .map((worker) => ({
      lat: toMapCoordinate(worker.latitude),
      lng: toMapCoordinate(worker.longitude),
    }))
    .filter((entry): entry is { lat: number; lng: number } => entry.lat != null && entry.lng != null)

  if (coordinates.length === 0) {
    const span = 0.12
    const west = (fallbackLng - span).toFixed(6)
    const south = (fallbackLat - span).toFixed(6)
    const east = (fallbackLng + span).toFixed(6)
    const north = (fallbackLat + span).toFixed(6)
    return `${west}%2C${south}%2C${east}%2C${north}`
  }

  const lats = coordinates.map((entry) => entry.lat)
  const lngs = coordinates.map((entry) => entry.lng)
  const minLat = Math.min(...lats)
  const maxLat = Math.max(...lats)
  const minLng = Math.min(...lngs)
  const maxLng = Math.max(...lngs)
  const latPadding = Math.max((maxLat - minLat) * 0.25, 0.02)
  const lngPadding = Math.max((maxLng - minLng) * 0.25, 0.02)

  const west = (minLng - lngPadding).toFixed(6)
  const south = (minLat - latPadding).toFixed(6)
  const east = (maxLng + lngPadding).toFixed(6)
  const north = (maxLat + latPadding).toFixed(6)
  return `${west}%2C${south}%2C${east}%2C${north}`
}

function formatEntryTimeRange(entry: DashboardUpcomingWorkdayScheduleItem) {
  const normalized = formatRangeFromDateTimes(entry.planned_start, entry.planned_end)
  if (normalized) {
    return normalized
  }

  const start = entry.planned_start_local || entry.planned_start
  const end = entry.planned_end_local || entry.planned_end
  const label = `${toShortTime(start)}-${toShortTime(end)}`
  if (label === '00:00-00:00' || label === '-----') {
    return 'Schedule pending'
  }
  return label
}

function toShortTime(value?: string | null) {
  const raw = String(value || '').trim()
  if (!raw) {
    return '--'
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return '--'
  }

  const directMatch = raw.match(/\b(\d{2}:\d{2})/)
  if (directMatch?.[1]) {
    return directMatch[1]
  }

  const parsed = new Date(raw)
  if (Number.isNaN(parsed.getTime())) {
    return raw
  }

  return parsed.toISOString().slice(11, 16)
}

function formatRangeFromDateTimes(startValue?: string | null, endValue?: string | null) {
  const start = parseScheduleDate(startValue)
  const end = parseScheduleDate(endValue)
  if (!start || !end) {
    return null
  }

  const startMs = start.getTime()
  const endMs = end.getTime()
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    return null
  }

  const formatter = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  })

  return `${formatter.format(start)}-${formatter.format(end)}`
}

function parseScheduleDate(value?: string | null) {
  const raw = String(value || '').trim()
  if (!raw || /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return null
  }

  const parsed = new Date(raw)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}
